import type { ColDef } from 'ag-grid-enterprise';
import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';

import type { CellClickedEvent, ColGroupDef } from 'ag-grid-community';
import { upperFirst } from 'lodash-es';
import { useMixpanel } from '../../contexts';
import { MixpanelEvent } from '../../tokens';
import { ExportButton } from './ExportDialog';
import { columnTypes, getAgGridColId } from './columns';
import type { Column, ColumnDynamics } from './columns/types';
import { AG_GRID_BLOTTER_TOOLTIP_KEY, AG_GRID_RICH_HEADER_TOOLTIP_SENTINEL } from './tooltips/AgGridBlotterTooltip';
import type { ColumnDefsOptions } from './types';
import { isColumnGroup, type ColumnOrColumnGroup } from './types';
import type { ColumnDynamicsSpec } from './useColumnDynamics';
import { useColumnDynamics } from './useColumnDynamics';
import type { UsePersistedBlotterTable } from './usePersistedBlotterTable';

function getColumnGroupDynamics(column: ColumnOrColumnGroup): ColumnDynamics | undefined {
  return isColumnGroup(column)
    ? (column.dynamics ?? column.children.find(child => child.dynamics)?.dynamics)
    : undefined;
}

function applyColumnGroupHeaderTooltipFallback(
  colGroupDef: ColGroupDef & { richHeaderTooltip?: ReactElement }
): ColGroupDef {
  const { richHeaderTooltip, ...agGridColGroupDef } = colGroupDef;

  if (agGridColGroupDef.tooltipComponent != null) {
    return agGridColGroupDef;
  }

  const hasHeaderTooltip = agGridColGroupDef.headerTooltip != null;
  const hasRichHeaderTooltip = richHeaderTooltip != null;

  if (hasHeaderTooltip || hasRichHeaderTooltip) {
    return {
      ...agGridColGroupDef,
      tooltipComponent: AG_GRID_BLOTTER_TOOLTIP_KEY,
      headerTooltip: hasHeaderTooltip ? agGridColGroupDef.headerTooltip : AG_GRID_RICH_HEADER_TOOLTIP_SENTINEL,
      ...(hasRichHeaderTooltip
        ? { tooltipComponentParams: { ...agGridColGroupDef.tooltipComponentParams, richHeaderTooltip } }
        : {}),
    };
  }

  return agGridColGroupDef;
}

export function useColumnDefs<R>(
  columns: ColumnOrColumnGroup[],
  {
    handleClickJson,
    exportDataAsCSV,
    columnDynamicsSpecs,
    persistence,
  }: ColumnDefsOptions<R> & {
    columnDynamicsSpecs?: ColumnDynamicsSpec[];
    persistence?: UsePersistedBlotterTable;
  } = {}
): Array<ColDef<R> | ColGroupDef<R>> {
  const mixpanel = useMixpanel();
  const hydratedColumns = useColumnDynamics({
    columns,
    persistedColumnDynamics: persistence?.state?.extraGridState?.columnDynamics,
    specs: columnDynamicsSpecs,
  });

  const convertColumnToColDef = useCallback(
    (column: Column): ColDef => {
      // If there is no type, we error. All column definitions should have a type.
      if (!('type' in column && column.type in columnTypes)) {
        throw new Error(`No column type specified for column ${JSON.stringify(column)}`);
      }

      // Call the column factory
      const colDef: ColDef = columnTypes[column.type](column);

      // If the colDef headerName is undefined (and there's a type defined -- see above), set a prettified headerName derived from the type
      if (colDef.headerName === undefined) {
        // "LimitID" -> "Limit ID"
        colDef.headerName = getAgGridColId(colDef)
          .split(/(?<=[a-z])(?=[A-Z])/)
          .map(upperFirst)
          .join(' ');
      }

      // Below here, we mutate specific columns to attach certain event handlers, etc.

      if (column.type === 'hamburgerMenu' && column.params.csvExport && exportDataAsCSV) {
        // For hamburger columns, if csvExport is true, set exportDataAsCsv button as header component
        const wrappedExportDataAsCSV = () => {
          exportDataAsCSV({});
          mixpanel.track(MixpanelEvent.ExportRows);
        };

        colDef.width = 100;
        colDef.headerComponent = () => <ExportButton onExportAction={wrappedExportDataAsCSV} />;
      } else if (column.type === 'json' && handleClickJson) {
        // For json, attach the handleClickJson param passed to useBlotterTable to the onClick handler of the ColDef
        colDef.onCellClicked = ({ data }: CellClickedEvent<R>) => handleClickJson(data);
      }

      if (column.dynamics) {
        const dynamicsSpec = columnDynamicsSpecs?.find(spec => spec.type === column.dynamics?.spec);
        Object.assign(colDef, dynamicsSpec?.getColDefOverlay?.(column.dynamics));
      }

      return colDef;
    },
    [columnDynamicsSpecs, exportDataAsCSV, handleClickJson, mixpanel]
  );

  const convertedColumnDefs = useMemo(() => {
    return hydratedColumns.map(column => {
      if (isColumnGroup(column)) {
        const colGroupDef = { ...column, children: column.children.map(convertColumnToColDef) };
        const dynamics = getColumnGroupDynamics(column);
        if (dynamics) {
          const dynamicsSpec = columnDynamicsSpecs?.find(spec => spec.type === dynamics.spec);
          Object.assign(colGroupDef, dynamicsSpec?.getColDefOverlay?.(dynamics));
        }

        return applyColumnGroupHeaderTooltipFallback(colGroupDef);
      }

      return convertColumnToColDef(column);
    });
  }, [columnDynamicsSpecs, hydratedColumns, convertColumnToColDef]);

  return convertedColumnDefs;
}
